/**
 * @fileoverview Custom job-contact roles manager
 *
 * Lets any org member create their own job-contact roles (name, colour,
 * optional icon, optional description) that sit alongside the built-in ones
 * (Director, Secured Creditor, …) so contacts can be classified more precisely
 * than the broad "Other". Roles are assigned to job contacts from the CRM;
 * precedent templates can fan out documents over a custom role via `jcr:<id>`
 * tags just like a built-in role.
 *
 * Custom roles are always practitioner-assigned — the reconcile pipeline never
 * derives them from agent sources. Visual language mirrors the Firm Contact
 * Categories manager and reuses the same colour/icon swatch pickers and badge.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { TaskColor } from "@cvx/lib/taskColor.schema";
import type { WorkflowRoleIcon } from "@cvx/tasks/workflowRoles/schema";
import { WORKFLOW_ROLE_DESCRIPTION_MAX_LENGTH as DESCRIPTION_MAX_LENGTH } from "@cvx/tasks/roleFields";
import {
  ArrowDown,
  ArrowUp,
  GripVertical,
  Loader2,
  Lock,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/ui/composed/overlays/alert-dialog";
import { Badge, Button } from "@/ui/primitives";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { cn } from "@/utils/misc";
import { ColorSwatchPicker } from "@/components/settings/tasks/ColorSwatchPicker";
import { IconSwatchPicker } from "@/components/settings/tasks/IconSwatchPicker";
import { RoleBadge } from "@/components/settings/tasks/RoleBadge";
import { ROLE_META, ROLE_ORDER } from "./types";

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

interface CustomRole {
  _id: Id<"jobContactRoles">;
  name: string;
  color: TaskColor;
  icon?: WorkflowRoleIcon;
  description?: string;
  sortOrder: number;
}

export function JobContactRolesManager({
  orgId,
  canEdit,
}: {
  orgId: Id<"organizations">;
  canEdit: boolean;
}) {
  const { data: roles } = useQuery(
    convexQuery(api.contacts.customRoles.listJobContactRoles, { orgId }),
  );
  const reorder = useConvexMutation(
    api.contacts.customRoles.reorderJobContactRoles,
  );
  const [showNewForm, setShowNewForm] = useState(false);
  const rows: CustomRole[] = useMemo(() => roles ?? [], [roles]);

  const orderedRows = useMemo(
    () => [...rows].sort((a, b) => a.sortOrder - b.sortOrder),
    [rows],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = orderedRows.map((r) => r._id);
    const from = ids.indexOf(active.id as Id<"jobContactRoles">);
    const to = ids.indexOf(over.id as Id<"jobContactRoles">);
    if (from < 0 || to < 0) return;
    reorder({ orgId, orderedIds: arrayMove(ids, from, to) }).catch(
      (err: Error) => toast.error(err.message || "Could not reorder roles."),
    );
  };

  return (
    <div className="flex w-full flex-col gap-6">
      {!canEdit && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200/60 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
          <Lock className="mt-0.5 size-4 shrink-0" />
          <div className="text-sm">
            <p className="font-medium">Read-only view</p>
            <p className="mt-0.5 text-xs opacity-80">
              You need to be a member of this organisation to manage contact
              roles.
            </p>
          </div>
        </div>
      )}

      <div className={GROUP_CARD_CLASS}>
        <div className="flex items-start justify-between gap-6 border-b border-border/50 p-6">
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold text-primary">
              Contact Roles
            </h1>
            <p className="mt-1 text-sm text-primary/60">
              Create your own roles for job contacts alongside the built-in ones
              — for example a "Tax Advisor" where "Other" is too broad. Custom
              roles can be filtered, displayed, and used to pre-select day-one
              documents just like the built-ins. They are always assigned
              manually and never changed by the analysis agents.
            </p>
          </div>
          <Badge variant="secondary" className="shrink-0">
            {rows.length} {rows.length === 1 ? "role" : "roles"}
          </Badge>
        </div>

        <div className="flex items-center justify-between border-b border-border/50 px-6 py-4">
          <div>
            <h4 className="text-sm font-semibold text-primary">Custom roles</h4>
            <p className="text-xs text-primary/40">
              Drag to reorder — this is the order they appear in pickers.
            </p>
          </div>
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowNewForm(true)}
              className="gap-1.5"
            >
              <Plus className="size-3.5" />
              Add role
            </Button>
          )}
        </div>

        <div className="divide-y divide-border/30">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedRows.map((r) => r._id)}
              strategy={verticalListSortingStrategy}
            >
              {orderedRows.map((role, index) => (
                <RoleRow
                  key={role._id}
                  orgId={orgId}
                  role={role}
                  canEdit={canEdit}
                  isFirst={index === 0}
                  isLast={index === orderedRows.length - 1}
                  orderedIds={orderedRows.map((r) => r._id)}
                  flatIndex={index}
                />
              ))}
            </SortableContext>
          </DndContext>

          {rows.length === 0 && !showNewForm && (
            <div className="px-6 py-8 text-center">
              <p className="text-sm text-primary/40">
                No custom roles yet.
                {canEdit
                  ? " Add roles like Tax Advisor, Valuer or Landlord to classify contacts."
                  : ""}
              </p>
            </div>
          )}

          {showNewForm && (
            <NewRoleForm orgId={orgId} onClose={() => setShowNewForm(false)} />
          )}
        </div>
      </div>

      {/* Built-in roles, for reference — not editable. */}
      <div className={GROUP_CARD_CLASS}>
        <div className="border-b border-border/50 px-6 py-4">
          <h4 className="text-sm font-semibold text-primary">Built-in roles</h4>
          <p className="text-xs text-primary/40">
            Always available; custom roles appear alongside these.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 px-6 py-4">
          {ROLE_ORDER.map((key) => {
            const meta = ROLE_META[key];
            return (
              <Badge
                key={key}
                color={meta.badgeColor}
                variant="soft"
                className="gap-1"
              >
                <meta.icon className="size-3 shrink-0" />
                {meta.label}
              </Badge>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Role rows
// ---------------------------------------------------------------------------

function RoleRow({
  orgId,
  role,
  canEdit,
  isFirst,
  isLast,
  orderedIds,
  flatIndex,
}: {
  orgId: Id<"organizations">;
  role: CustomRole;
  canEdit: boolean;
  isFirst: boolean;
  isLast: boolean;
  orderedIds: Array<Id<"jobContactRoles">>;
  flatIndex: number;
}) {
  const [editing, setEditing] = useState(false);
  const update = useConvexMutation(
    api.contacts.customRoles.updateJobContactRole,
  );
  const reorder = useConvexMutation(
    api.contacts.customRoles.reorderJobContactRoles,
  );
  const archive = useConvexMutation(
    api.contacts.customRoles.archiveJobContactRole,
  );

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: role._id, disabled: !canEdit || editing });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const move = (direction: -1 | 1) => {
    const next = [...orderedIds];
    const target = flatIndex + direction;
    if (target < 0 || target >= next.length) return;
    [next[flatIndex], next[target]] = [next[target], next[flatIndex]];
    reorder({ orgId, orderedIds: next }).catch((e: Error) =>
      toast.error(e.message || "Could not reorder roles."),
    );
  };

  if (editing) {
    return (
      <div ref={setNodeRef} style={style} className="px-6 py-4">
        <RoleForm
          initial={{
            name: role.name,
            color: role.color,
            icon: role.icon ?? null,
            description: role.description ?? "",
          }}
          submitLabel="Save"
          onCancel={() => setEditing(false)}
          onSubmit={async (state) => {
            await update({
              roleId: role._id,
              patch: {
                name: state.name.trim(),
                color: state.color,
                // null clears the icon; "" clears the description.
                icon: state.icon,
                description: state.description.trim(),
              },
            });
            setEditing(false);
          }}
        />
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-3 bg-background px-6 py-3",
        isDragging && "relative z-10 shadow-lg",
      )}
    >
      {canEdit && (
        <span
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          className="cursor-grab touch-none text-primary/25 hover:text-primary/60 active:cursor-grabbing"
          title="Drag to reorder"
          aria-label={`Drag ${role.name} to reorder`}
        >
          <GripVertical className="size-4" />
        </span>
      )}
      {canEdit && (
        <div className="flex flex-col">
          <button
            type="button"
            disabled={isFirst}
            onClick={() => move(-1)}
            className="text-primary/30 hover:text-primary/70 disabled:opacity-30"
            aria-label="Move up"
          >
            <ArrowUp className="size-3" />
          </button>
          <button
            type="button"
            disabled={isLast}
            onClick={() => move(1)}
            className="text-primary/30 hover:text-primary/70 disabled:opacity-30"
            aria-label="Move down"
          >
            <ArrowDown className="size-3" />
          </button>
        </div>
      )}
      <RoleBadge role={role} className="shrink-0" />
      <div className="min-w-0 flex-1">
        {role.description && (
          <p className="truncate text-xs text-primary/50">{role.description}</p>
        )}
      </div>
      {canEdit && (
        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="size-7"
            onClick={() => setEditing(true)}
          >
            <Pencil className="size-3.5" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="size-7 text-destructive hover:text-destructive"
                aria-label={`Remove role ${role.name}`}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove this role?</AlertDialogTitle>
                <AlertDialogDescription>
                  &ldquo;{role.name}&rdquo; will be archived and removed from
                  the role pickers. Contacts already assigned this role will no
                  longer show it. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    archive({ roleId: role._id }).catch((e: Error) =>
                      toast.error(e.message || "Could not remove role."),
                    );
                  }}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Remove
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </div>
  );
}

function NewRoleForm({
  orgId,
  onClose,
}: {
  orgId: Id<"organizations">;
  onClose: () => void;
}) {
  const create = useConvexMutation(
    api.contacts.customRoles.createJobContactRole,
  );
  return (
    <div className="bg-muted/20 px-6 py-4">
      <RoleForm
        initial={{ name: "", color: "gray", icon: null, description: "" }}
        submitLabel="Add role"
        onCancel={onClose}
        onSubmit={async (state) => {
          await create({
            orgId,
            name: state.name.trim(),
            color: state.color,
            icon: state.icon ?? undefined,
            description: state.description.trim() || undefined,
          });
          onClose();
        }}
      />
    </div>
  );
}

interface RoleFormState {
  name: string;
  color: TaskColor;
  icon: WorkflowRoleIcon | null;
  description: string;
}

function RoleForm({
  initial,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  initial: RoleFormState;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (state: RoleFormState) => Promise<void>;
}) {
  const [state, setState] = useState<RoleFormState>(initial);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!state.name.trim()) {
      toast.error("Role name is required.");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(state);
    } catch (e) {
      const message = e instanceof Error ? e.message : undefined;
      toast.error(message || "Could not save role.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs font-medium text-muted-foreground">
          Name
        </Label>
        <Input
          value={state.name}
          autoFocus
          onChange={(e) => setState({ ...state, name: e.target.value })}
          placeholder="Tax Advisor, Valuer, Landlord…"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-xs font-medium text-muted-foreground">
          Colour
        </Label>
        <ColorSwatchPicker
          value={state.color}
          onChange={(color) => setState({ ...state, color })}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-xs font-medium text-muted-foreground">
          Icon
        </Label>
        <IconSwatchPicker
          value={state.icon}
          onChange={(icon) => setState({ ...state, icon })}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-xs font-medium text-muted-foreground">
          Description
          <span className="ml-1 font-normal opacity-70">(optional)</span>
        </Label>
        <Input
          value={state.description}
          maxLength={DESCRIPTION_MAX_LENGTH}
          onChange={(e) => setState({ ...state, description: e.target.value })}
          placeholder="What kind of contacts belong in this role"
        />
        <p className="text-xs text-muted-foreground">
          Shown as a tooltip wherever this role appears.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-md bg-muted/30 px-3 py-2">
        <span className="text-xs text-muted-foreground">Preview</span>
        <RoleBadge
          role={{
            name: state.name.trim() || "Role name",
            color: state.color,
            icon: state.icon ?? undefined,
            description: state.description.trim() || undefined,
          }}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} className="gap-1">
          <X className="size-3.5" />
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={submit}
          disabled={submitting}
          className="gap-1.5"
        >
          {submitting ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Plus className="size-3.5" />
          )}
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}
